import { Component, computed, inject, OnInit } from '@angular/core';


import { UserProfileStore } from '../store/user-profile.store';
import { SessionItemComponent } from './session-item.component';
import { ErrorAlertComponent } from '../../alerts';
import { RefreshButtonComponent } from '../../buttons/refresh-button.component';

@Component({
  standalone: true,
  imports: [
    SessionItemComponent,
    ErrorAlertComponent,
    RefreshButtonComponent
],
  selector: 'sb-sessions-list',
  template: `
    <div class="space-y-6">
      <!-- Section Header -->
      <div class="animate-slideInUp flex flex-col gap-y-2">
        <h3 class="text-lg font-medium text-gray-900">Active Sessions</h3>
        <p class="text-sm text-gray-600">
          Manage your active sessions. You can revoke access to any session that
          you don't recognize.
        </p>

        <div class="flex justify-end">
          <lib-refresh-button (refresh)="loadSessions()" />
        </div>
      </div>

      <!-- Loading State -->
      @if (isLoading()) {
      <div class="animate-pulse space-y-4">
        @for (item of [1,2,3]; track item) {
        <div class="bg-gray-200 rounded-lg h-20"></div>
        }
      </div>
      }

      <!-- Error State -->
      @if (sessionsError()) {
      <div class="rounded-md bg-red-50 p-4 animate-slideInUp">
        <lib-error-alert [error]="sessionsError()" />
      </div>
      }

      <!-- Sessions List -->
      @if (!isLoading() && !sessionsError() && sessions().length > 0) {
      <div
        class="animate-slideInUp flex flex-col gap-2 max-h-[400px] overflow-y-auto"
      >
        @for (session of sessions(); track session.session_id) {
        <sb-session-item
          [session]="session"
          (invalidateSession)="onInvalidateSession($event)"
        />
        }
      </div>
      }

      <!-- Empty State -->
      @if (!isLoading() && !sessionsError() && sessions().length === 0) {
      <div class="text-center py-12 animate-slideInUp">
        <svg
          class="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
          />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">
          No active sessions
        </h3>
        <p class="mt-1 text-sm text-gray-500">
          You don't have any active sessions at the moment.
        </p>
      </div>
      }
    </div>
  `,
})
export class SessionsListComponent implements OnInit {
  private userProfileStore = inject(UserProfileStore);

  isLoading = computed(() => this.userProfileStore.isLoadingSessions());
  sessions = computed(() => this.userProfileStore.sessions());
  sessionsError = computed(() => this.userProfileStore.sessionsError());

  /**
   * Initialize the component
   */
  ngOnInit() {
    this.loadSessions();
  }

  loadSessions() {
    this.userProfileStore.loadUserSessions();
  }

  async onInvalidateSession(sessionId: string) {
    try {
      await this.userProfileStore.invalidateSession(sessionId);
    } catch (error) {
      // Error is handled by the store
    }
  }
}
